/**
 * Browser TokenProvider — stores tokens in localStorage. The device
 * fingerprint is generated once per browser install and persisted
 * alongside.
 *
 * Production hardening (next session): bind tokens to a worker / move
 * refresh into an httpOnly cookie. For MVP localStorage is acceptable
 * because the access token is short-lived (15 min) and the refresh
 * token is device-fingerprint bound on the server side.
 */
import type { TokenProvider, LoginResponse, RefreshResponse } from "@dfp/api-client";

const ACCESS_KEY = "dfp.access";
const REFRESH_KEY = "dfp.refresh";
const FINGERPRINT_KEY = "dfp.deviceFingerprint";

function getOrMakeFingerprint(): string {
  let fp = localStorage.getItem(FINGERPRINT_KEY);
  if (!fp) {
    fp = crypto.randomUUID().replace(/-/g, "");
    localStorage.setItem(FINGERPRINT_KEY, fp);
  }
  return fp;
}

export const browserTokens: TokenProvider = {
  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_KEY);
  },
  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },
  getDeviceFingerprint(): string {
    return getOrMakeFingerprint();
  },
  saveTokens(tokens: LoginResponse | RefreshResponse): void {
    localStorage.setItem(ACCESS_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
  },
  clearTokens(): void {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};
